import FetchClient, { type ApiResponse } from "./FetchClient";
import type { User } from "./UserService";

interface DiscordLinkResponse {
  redirectUrl: string;
}

interface DiscordCompleteRequest {
  discordUserId: string;
  discordRefreshToken: string;
}

interface DiscordMessageResponse {
  message: string;
}

export interface SendToDiscordRequest {
  itemName: string;
  oldPrice: number;
  newPrice: number;
  upc: string;
  imageUrl: string;
  storeName: string;
  description: string;
  textChannel: string;
  includeBarcode: string;
  retailer?: string; // "walmart" or "home_depot"
  zipCode?: string;
}

export interface SendToDiscordResponse {
  message: string;
}

export async function linkDiscord(): Promise<DiscordLinkResponse> {
  const response = await FetchClient.post<ApiResponse<DiscordLinkResponse>>(
    "/discord/link",
    {}
  );
  if (!response.data?.redirectUrl) {
    throw new Error("Discord redirect URL missing");
  }
  return response.data;
}

export async function syncDiscord(): Promise<DiscordMessageResponse> {
  const response = await FetchClient.post<ApiResponse<DiscordMessageResponse>>(
    "/discord/sync",
    {}
  );
  return response.data;
}

export async function unlinkDiscord(): Promise<DiscordMessageResponse> {
  const response = await FetchClient.post<ApiResponse<DiscordMessageResponse>>(
    "/discord/unlink",
    {}
  );
  return response.data;
}

export async function completeDiscordLink(
  data: DiscordCompleteRequest
): Promise<DiscordMessageResponse> {
  const response = await FetchClient.post<ApiResponse<DiscordMessageResponse>>(
    "/discord/complete-link",
    data
  );
  return response.data;
}

// Helper function to check if user has Discord linked
export function isDiscordLinked(user: User | null): boolean {
  return !!(user?.discordUserId && user?.discordRefreshToken);
}

export interface DiscordMembershipStatus {
  linked: boolean;
  inGuild: boolean;
  hasRole: boolean;
  ok: boolean;
}

// Checks whether the authenticated user has Discord linked, is a member of
// the DealHawk Discord guild, and holds the default member role. Used to gate
// Discord-only UI and prompt the user to re-sync if anything is off.
export async function getDiscordMembership(): Promise<DiscordMembershipStatus> {
  const response = await FetchClient.get<ApiResponse<DiscordMembershipStatus>>(
    "/discord/membership"
  );
  return response.data;
}

export async function sendToDiscord(
  data: SendToDiscordRequest
): Promise<SendToDiscordResponse> {
  const response = await FetchClient.post<ApiResponse<SendToDiscordResponse>>(
    "/send-to-discord",
    data
  );
  return response.data;
}
